import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Layout,
  LayoutGrid,
  ShoppingCart,
  ArrowUp,
  ArrowDown,
  Loader2,
  Save,
  RefreshCw,
  Globe,
  Eye,
  EyeOff,
  GripVertical,
  CheckCircle2,
  Settings,
  Layers,
  Monitor,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Placement types with descriptions
const PLACEMENT_TYPES = [
  {
    id: 'top_slot',
    name: 'Top of Category',
    description: 'Featured position at the top of the category page',
    icon: ArrowUp,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  {
    id: 'inline_slot',
    name: 'Inline (Within Listings)',
    description: 'Appears within the product/service listings',
    icon: LayoutGrid,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  {
    id: 'cart_upsell',
    name: 'Post-Checkout Upsell',
    description: 'Suggested after adding items to cart or completing purchase',
    icon: ShoppingCart,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
];

// Service categories
const SPORTS_SUBCATEGORIES = [
  { id: 'sports_equipment', name: 'Sports Equipment', icon: '🏀' },
  { id: 'fitness_gym', name: 'Fitness & Gym', icon: '💪' },
  { id: 'outdoor_adventure', name: 'Outdoor & Adventure', icon: '🏕️' },
  { id: 'cycling', name: 'Cycling', icon: '🚴' },
  { id: 'fishing_hunting', name: 'Fishing & Hunting', icon: '🎣' },
  { id: 'creative_hobbies', name: 'Creative Hobbies', icon: '🎨' },
  { id: 'gaming_esports', name: 'Gaming & E-Sports', icon: '🎮' },
  { id: 'water_sports', name: 'Water Sports', icon: '🏄' },
];

const AdminPlacementControls = () => {
  const [selectedCountry, setSelectedCountry] = useState('SL');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState('sports_equipment');

  useEffect(() => {
    fetchSettings();
  }, [selectedCountry]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/sports-services/admin/settings/${selectedCountry}`
      );
      setSettings(response.data);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      // Initialize with defaults if not found
      setSettings({
        country: selectedCountry,
        show_top_slot: true,
        show_inline_slot: true,
        show_cart_upsell: true,
        subcategory_placements: {},
        service_placements: {},
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/sports-services/admin/settings/${selectedCountry}`,
        settings
      );
      alert('Placement settings saved successfully!');
    } catch (err) {
      console.error('Failed to save settings:', err);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleGlobalPlacement = (placementId) => {
    setSettings(prev => ({
      ...prev,
      [`show_${placementId}`]: !prev[`show_${placementId}`]
    }));
  };

  const toggleSubcategoryPlacement = (subcatId, placementId) => {
    setSettings(prev => {
      const subcatPlacements = prev.subcategory_placements || {};
      const currentSubcat = subcatPlacements[subcatId] || {
        top_slot: true,
        inline_slot: true,
        cart_upsell: true,
      };
      
      return {
        ...prev,
        subcategory_placements: {
          ...subcatPlacements,
          [subcatId]: {
            ...currentSubcat,
            [placementId]: !currentSubcat[placementId]
          }
        }
      };
    });
  };

  const getSubcategoryPlacement = (subcatId, placementId) => {
    const subcatPlacements = settings?.subcategory_placements?.[subcatId];
    if (subcatPlacements && subcatPlacements[placementId] !== undefined) {
      return subcatPlacements[placementId];
    }
    // Fall back to global setting
    return settings?.[`show_${placementId}`] !== false;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-placement-controls">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Layout className="h-6 w-6 text-blue-600" />
            Service Card Placement
          </h2>
          <p className="text-gray-500">Control where service cards appear in the marketplace</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
              <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchSettings} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
          <Button onClick={handleSaveSettings} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Placement Preview */}
      <Card className="bg-gradient-to-r from-gray-50 to-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Placement Preview
          </CardTitle>
          <CardDescription>Visual representation of where service cards appear</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {PLACEMENT_TYPES.map((placement) => {
              const Icon = placement.icon;
              const isEnabled = settings?.[`show_${placement.id}`] !== false;
              
              return (
                <div
                  key={placement.id}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isEnabled 
                      ? `${placement.bgColor} border-${placement.color.replace('text-', '')}` 
                      : 'bg-gray-100 border-gray-200 opacity-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon className={`h-5 w-5 ${isEnabled ? placement.color : 'text-gray-400'}`} />
                      <span className="font-medium">{placement.name}</span>
                    </div>
                    <Badge variant={isEnabled ? 'default' : 'secondary'}>
                      {isEnabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">{placement.description}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="global">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="global">
            <Globe className="h-4 w-4 mr-1" />
            Global Settings
          </TabsTrigger>
          <TabsTrigger value="subcategory">
            <Layers className="h-4 w-4 mr-1" />
            By Subcategory
          </TabsTrigger>
        </TabsList>

        {/* Global Settings Tab */}
        <TabsContent value="global" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Global Placement Settings</CardTitle>
              <CardDescription>
                These settings apply to all subcategories unless overridden
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {PLACEMENT_TYPES.map((placement) => {
                const Icon = placement.icon;
                const isEnabled = settings?.[`show_${placement.id}`] !== false;
                
                return (
                  <div
                    key={placement.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${placement.bgColor}`}>
                        <Icon className={`h-6 w-6 ${placement.color}`} />
                      </div>
                      <div>
                        <h4 className="font-medium">{placement.name}</h4>
                        <p className="text-sm text-gray-500">{placement.description}</p>
                      </div>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => toggleGlobalPlacement(placement.id)}
                    />
                  </div>
                );
              })}

              {/* Device-specific settings */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Device-Specific Options
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Monitor className="h-4 w-4 text-gray-600" />
                      <span>Show on Desktop</span>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Smartphone className="h-4 w-4 text-gray-600" />
                      <span>Show Mobile Sticky Pill</span>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subcategory Settings Tab */}
        <TabsContent value="subcategory" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Subcategory Placement Overrides</CardTitle>
              <CardDescription>
                Configure placement settings for individual subcategories
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                {SPORTS_SUBCATEGORIES.map((subcat) => (
                  <Button
                    key={subcat.id}
                    variant={selectedSubcategory === subcat.id ? 'default' : 'outline'}
                    className="justify-start"
                    onClick={() => setSelectedSubcategory(subcat.id)}
                  >
                    <span className="mr-2">{subcat.icon}</span>
                    {subcat.name}
                  </Button>
                ))}
              </div>

              {/* Selected subcategory settings */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-4">
                  {SPORTS_SUBCATEGORIES.find(s => s.id === selectedSubcategory)?.icon}{' '}
                  {SPORTS_SUBCATEGORIES.find(s => s.id === selectedSubcategory)?.name} Placements
                </h4>
                <div className="space-y-3">
                  {PLACEMENT_TYPES.map((placement) => {
                    const Icon = placement.icon;
                    const isEnabled = getSubcategoryPlacement(selectedSubcategory, placement.id);
                    const globalEnabled = settings?.[`show_${placement.id}`] !== false;
                    
                    return (
                      <div
                        key={placement.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isEnabled ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className={`h-5 w-5 ${isEnabled ? placement.color : 'text-gray-400'}`} />
                          <div>
                            <p className="font-medium">{placement.name}</p>
                            {!globalEnabled && (
                              <p className="text-xs text-yellow-600">Global setting: Disabled</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isEnabled && <Badge variant="outline" className="text-xs">Active</Badge>}
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={() => toggleSubcategoryPlacement(selectedSubcategory, placement.id)}
                            disabled={!globalEnabled}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Services list for this subcategory */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-500" />
                    Service-Level Controls
                  </h5>
                  <p className="text-sm text-gray-500 mb-4">
                    Fine-tune placement for individual services in this category
                  </p>
                  <div className="space-y-2">
                    {/* Example services - in production would fetch from API */}
                    {[
                      { id: 'coaching_individual', name: 'Individual Sports Coaching', tier: 'core' },
                      { id: 'coaching_group', name: 'Group Sports Training', tier: 'core' },
                      { id: 'equipment_repair', name: 'Equipment Repair', tier: 'premium' },
                    ].map((service) => (
                      <div
                        key={service.id}
                        className="flex items-center justify-between p-2 bg-white rounded border"
                      >
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 text-gray-300 cursor-move" />
                          <span className="text-sm">{service.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {service.tier}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" className="h-7 px-2">
                            <ArrowUp className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2">
                            <ArrowDown className="h-3 w-3" />
                          </Button>
                          <Switch defaultChecked />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Usage Guide */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-medium text-blue-900 flex items-center gap-2 mb-2">
            <Eye className="h-4 w-4" />
            Placement Guide
          </h4>
          <div className="text-sm text-blue-700 space-y-2">
            <p><strong>Top of Category:</strong> Best for high-conversion services and promotions. Shows above all listings.</p>
            <p><strong>Inline:</strong> Natural placement within search results. Good for general visibility.</p>
            <p><strong>Post-Checkout Upsell:</strong> Triggered after purchase. Ideal for related services (e.g., bought a bike? Add assembly service).</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPlacementControls;
